import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import {
  catchError,
  defer,
  first,
  from,
  map,
  Observable,
  switchMap,
  tap,
  throwError,
} from 'rxjs';
import { User } from 'firebase/auth';
import { OpenAuthClient, injectOpenAuth } from '@open/ngx-firebase/auth';
import { LoginRequest } from '@assets/requests/auth/Login.request';
import { UserModel } from '@models/user/User.model';
import { UserService } from '@services/user.service';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  #userService: UserService = inject(UserService);
  #router: Router = inject(Router);
  #route: ActivatedRoute = inject(ActivatedRoute);
  readonly #client: OpenAuthClient<UserModel> = injectOpenAuth<UserModel>({
    appKey: 'admin',
    markActivatedOnPasswordUpdate: false,
    markActivatedOnPinLogin: false,
  });

  user$: Observable<User | null> = this.#client.user$;

  isLoggedIn$: Observable<boolean> = this.user$.pipe(
    map((user: User | null) => !!user),
  );

  constructor() {
    // Session-driven context: also covers a page refresh with a persisted session,
    // not just the login flow below.
    this.user$.pipe(
      switchMap((user: User | null) => !!user ? this.onLogin$() : this.#onLogout$()),
    ).subscribe();
  }

  login$(request: LoginRequest): Observable<void> {
    const { userName, password }: LoginRequest = request;
    return this.#client.login$(userName, password)
      .pipe(
        switchMap(() => this.#route.queryParams),
        first(),
        tap((params: { redirect?: string }) => {
          if (params.redirect) {
            void this.#router.navigateByUrl(params.redirect);
          } else {
            void this.#router.navigate(['/']);
          }
        }),
        map(() => void 0));
  }

  async logout(): Promise<void> {
    await this.#router.navigate(['/']);
    void this.#client.logout();
    this.#userService.clearUser();
  }

  onLogin$(): Observable<void> {
    return this.user$.pipe(
      first(),
      switchMap((afUser: User | null) => {
        if (!afUser) {
          throw new Error('Login with FB failed');
        }
        return this.#userService.initUserById$(afUser.uid);
      }),
      catchError((e) => {
        this.logout();
        return throwError(() => e);
      }),
    );
  }

  #onLogout$(): Observable<void> {
    return defer(() => {
      this.#userService.clearUser();
      return from(this.#router.navigate(['/auth/login'], { queryParams: { redirect: this.#router.url } }))
        .pipe(
          map(() => void 0),
        );
    });
  }
}
